import { useListingState } from "src/hooks/stores/use-listing-store";
import type { ProductListType } from "src/types/product-listing";

const CNSTRC_FILTER_NAME_BY_PLP_TYPE: Partial<Record<ProductListType, string>> =
  {
    BRAND_PLP: "brand",
    CATEGORY_PLP: "group_id",
    SHOP_TYPE_PLP: "group_id",
    COLLECTIONS_PLP: "collection_id",
  };

type BeaconFilterValues = {
  slug: string;
  categoryId?: string;
};

export function getBrowseBeaconFilter(
  pageContentType: ProductListType,
  { slug, categoryId }: BeaconFilterValues
): { name: string; value: string } | null {
  const name = CNSTRC_FILTER_NAME_BY_PLP_TYPE[pageContentType];

  if (!name) {
    return null;
  }

  const value =
    pageContentType === "CATEGORY_PLP" || pageContentType === "SHOP_TYPE_PLP"
      ? categoryId
      : slug;

  return value ? { name, value } : null;
}

type CnstrcListingMarkerProps = {
  pageContentType: ProductListType;
  query?: string;
  slug: string;
  categoryId?: string;
  children: React.ReactNode;
};

function CnstrcListingMarker({
  pageContentType,
  query,
  slug,
  categoryId,
  children,
}: CnstrcListingMarkerProps) {
  const { page, totalResults, resultId, request } = useListingState();
  const isSearch = pageContentType === "SEARCH_PLP";
  const beaconFilter = isSearch
    ? null
    : getBrowseBeaconFilter(pageContentType, {
        slug,
        categoryId,
      });

  return (
    <div
      style={{ display: "contents" }}
      {...(isSearch
        ? { "data-cnstrc-search": "" }
        : { "data-cnstrc-browse": "" })}
      {...(isSearch && query ? { "data-cnstrc-search-term": query } : {})}
      data-cnstrc-num-results={totalResults}
      data-cnstrc-result-page={page + 1}
      {...(beaconFilter
        ? {
            "data-cnstrc-filter-name": beaconFilter.name,
            "data-cnstrc-filter-value": beaconFilter.value,
          }
        : {})}
      {...(!isSearch && totalResults === 0
        ? { "data-cnstrc-zero-result": "" }
        : {})}
      data-cnstrc-result-id={resultId}
    >
      {/*
        Constructor's tracking beacon reads `request` + `result_id` from this
        tag to attribute search/browse results. Rendered only when Constructor
        powered the results (a `result_id` is present) so Algolia-powered
        listings — and every non-PLP page, which has no marker — are excluded.
        `<` is escaped so a stray "</script>" in the payload can't break out.
      */}
      {resultId ? (
        <script
          id="cnstrc-data"
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              request: request ?? {},
              result_id: resultId,
            }).replace(/</g, "\\u003c"),
          }}
        />
      ) : null}
      {children}
    </div>
  );
}

export { CnstrcListingMarker };
